// La feuille de saisie — ce qu'on emporte DEVANT COL MNC.
//
// L'étape 11 dicte déjà tout cela à l'écran. Alors pourquoi du papier ?
//
// Parce que la saisie ne se fait pas ici. Elle se fait dans un autre logiciel,
// souvent sur un autre poste, et le va-et-vient entre deux écrans est
// exactement ce qui fait sauter une ligne ou en coller deux fois. Le papier
// tient à côté du clavier, il ne se rafraîchit pas, il ne se met pas en veille,
// et on peut cocher dessus.
//
// CE MODULE NE CALCULE PRESQUE RIEN. Il compose ce que `colmnc.ts` produit
// déjà, dans l'ordre où Kamel remonte ses écrans, et n'ajoute que ce que
// l'écran ne montrait pas encore : les chèques hors LAD, les totaux LAD par
// mode de paiement, et le tri des entrées de coffre. Recalculer un total qui
// existe déjà, c'est fabriquer une seconde vérité — et c'est toujours celle du
// papier qu'on croit, parce qu'elle est dans la main.

import { isPadPayment } from 'sotcp-core/csvParser'
import {
  apportsFlottants, argentTrouve, bonsParGroupe, coffreMonnaie, ecartsDeCaisse,
  lad, livraisonsMonnaie, type Caissier, type LigneDictee, type PadEnAttente,
} from './colmnc'
import { etatPad } from './derive'
import { SEUILS_DEFAUT } from './operateurs'
import type { Rapport } from './types'

const cents = (n: number): number => +(Number(n) || 0).toFixed(2)

const objet = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
const tableau = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v) ? (v as Record<string, unknown>[]) : []

/** 2,00 € — la tolérance du rapport opérateur, en euros. */
export const SEUIL_ECART_CAISSE = SEUILS_DEFAUT.ecartTolere / 100

// ── 3 · Écarts de caisse ───────────────────────────────────────────────────

/**
 * LES DEUX NATURES N'ONT PAS LE MÊME SEUIL, et ce n'est pas une inconséquence.
 *
 * Un écart de CAISSE sous 2 € est du bruit de comptage : un caissier compte des
 * billets à la main toute la journée. COL MNC n'en veut pas, et les saisir
 * ferait quinze lignes à 0,30 € qui noieraient celle à −40 €.
 *
 * Un règlement NON CONFORME, lui, se saisit quel que soit son montant : ce
 * n'est pas une imprécision de comptage, c'est un poste refusé. Un bon Catalina
 * de 0,50 € refusé reste un bon refusé, et il doit apparaître.
 */
export function ecartsASaisir(r: Rapport | null, seuil = SEUIL_ECART_CAISSE): LigneDictee[] {
  return ecartsDeCaisse(r).filter(
    (l) => l.nature === 'reglement' || Math.abs(l.valeur) >= seuil,
  )
}

// ── 5 et 7 · LAD ───────────────────────────────────────────────────────────

export interface TotauxLad {
  /** Tous les PAD de la journée, au montant réellement entré. */
  totalJour: number
  nbJour: number
  /** Ceux réglés par chèque. */
  cheques: { nb: number; total: number }
  /** Ceux réglés par carte. */
  cartes: { nb: number; total: number }
  /** Ceux dont le mode n'a pas encore été statué au dépouillement. */
  indetermines: { nb: number; total: number }
  /** La file d'attente, toutes journées confondues. */
  attente: { nb: number; total: number; lignes: LigneDictee[] }
}

/**
 * LE MONTANT RETENU EST LE MONTANT RÉEL, pas celui d'ORIKA.
 *
 * `etatPad` porte déjà l'écart entre les deux quand le justificatif était
 * anormal : un PAD annoncé 120 € dont on n'a trouvé que 100 € entre pour 100 €.
 * C'est la règle de l'étape 11, et le papier ne peut pas en avoir une autre —
 * sinon la feuille et l'écran dictent deux chiffres pour la même ligne.
 *
 * Les PAD dont le mode n'est pas statué sont COMPTÉS À PART plutôt que rangés
 * d'office en carte ou en chèque. Un total qui ne se répartit pas se voit ; un
 * total mal réparti se saisit.
 */
export function totauxLad(r: Rapport | null, padAttente: PadEnAttente[]): TotauxLad {
  const pad = etatPad(r)
  const reel = (l: { montant: number; ecart: number }) => cents(l.montant + l.ecart)

  const par = (t: 'carte' | 'cheque') => {
    const l = pad.lignes.filter((x) => x.verif?.type === t)
    return { nb: l.length, total: cents(l.reduce((s, x) => s + reel(x), 0)) }
  }
  const inc = pad.lignes.filter((x) => x.verif?.type !== 'carte' && x.verif?.type !== 'cheque')

  const a = lad(padAttente)
  return {
    totalJour: cents(pad.lignes.reduce((s, l) => s + reel(l), 0)),
    nbJour: pad.lignes.length,
    cheques: par('cheque'),
    cartes: par('carte'),
    indetermines: { nb: inc.length, total: cents(inc.reduce((s, x) => s + reel(x), 0)) },
    attente: { nb: a.nb, total: a.total, lignes: a.lignes },
  }
}

// ── Le document « Chèques » ────────────────────────────────────────────────

/** CHQ (2) et CHQ MANUEL (3) — les deux règlements chèque de la V2. */
export const REGLEMENTS_CHEQUE = [2, 3]

export interface Anomalie {
  operateur: string
  ticket: string
  montant: number
  /** Refusé au dépouillement, ou jamais vu. */
  quoi: 'non conforme' | 'en attente'
  note: string
}

export interface Cheques {
  nb: number
  total: number
  manuels: { nb: number; total: number }
  anomalies: Anomalie[]
}

/**
 * Les chèques de la journée, LAD EXCLUS.
 *
 * Un PAD réglé par chèque est déjà compté dans le bordereau LAD ; le reprendre
 * ici le ferait remettre deux fois. `isPadPayment` est la même garde que partout
 * ailleurs — le règlement 62, ou le libellé P.A.D quand l'identifiant manque.
 *
 * LE DÉTAIL NE SORT QUE POUR CE QUI CLOCHE. Une remise de chèques conforme se
 * saisit avec deux chiffres : combien, et combien en tout. Lister les
 * quarante-sept lignes conformes fabriquerait une page qu'on ne lit pas, dans
 * laquelle la seule qui compte serait invisible.
 */
export function chequesDuJour(r: Rapport | null): Cheques {
  const verifs = objet(r?.verifications)
  const notes = objet(r?.notes)

  const liste = tableau(r?.payments).filter(
    (p) => REGLEMENTS_CHEQUE.includes(Number(p.reglementId))
      && !isPadPayment(p as { reglementId?: number | null; typeReglement?: string | null }),
  )
  const manuels = liste.filter((p) => Number(p.reglementId) === 3)

  const anomalies: Anomalie[] = []
  for (const p of liste) {
    const id = String(p.id ?? '')
    const v = verifs[id]
    // Convention V2 : `null` = validé, clé absente = jamais ouvert. Ni l'un ni
    // l'autre n'est une anomalie — le second est le cas d'une journée qu'on
    // n'a pas encore dépouillée, et il ne faut pas la peindre en rouge.
    const quoi: Anomalie['quoi'] | null =
      v === 'non_conforme' || v === 'rejected' ? 'non conforme'
        : v === 'en_attente' ? 'en attente' : null
    if (!quoi) continue
    anomalies.push({
      operateur: String(p.operateur ?? '—'),
      ticket: String(p.ticket ?? '—'),
      montant: cents(Number(p.montant) || 0),
      quoi,
      note: typeof notes[id] === 'string' ? (notes[id] as string).trim() : '',
    })
  }

  return {
    nb: liste.length,
    total: cents(liste.reduce((s, p) => s + (Number(p.montant) || 0), 0)),
    manuels: { nb: manuels.length, total: cents(manuels.reduce((s, p) => s + (Number(p.montant) || 0), 0)) },
    anomalies,
  }
}

// ── 7 · Section coffre ─────────────────────────────────────────────────────

export interface ColonneCoffre { titre: string; lignes: Array<{ cle: string; qte: number }> }

/**
 * Les trois colonnes de dénominations, DANS L'ORDRE DE COL MNC : pièces,
 * rouleaux, billets, de gauche à droite.
 *
 * J'avais d'abord lu « de droite à gauche » comme une consigne de RENDU et sorti
 * billets-rouleaux-pièces pour que la lecture inverse donne l'ordre demandé.
 * C'était l'ordre demandé, à l'envers. Corrigé sur retour de Kamel : l'ordre
 * annoncé est celui dans lequel les colonnes se posent, point.
 *
 * Une dénomination à zéro ne s'imprime pas — elle n'a rien à saisir. Mais elle
 * disparaît COLONNE PAR COLONNE : quarante rouleaux de 2 € sans une seule pièce
 * de 2 € est un cas normal, et masquer la ligne entière ferait disparaître les
 * rouleaux avec elles.
 */
export function colonnesCoffre(r: Rapport | null): ColonneCoffre[] {
  const c = coffreMonnaie(r)
  return [
    {
      titre: 'Pièces',
      lignes: c.pieces.map((l) => ({ cle: l.cle, qte: l.valeur })).filter((l) => l.qte > 0),
    },
    {
      titre: 'Rouleaux',
      lignes: c.pieces.map((l) => ({ cle: l.cle, qte: l.paire?.valeur ?? 0 })).filter((l) => l.qte > 0),
    },
    {
      titre: 'Billets',
      lignes: c.billets.map((l) => ({ cle: l.cle, qte: l.valeur })).filter((l) => l.qte > 0),
    },
  ]
}

/**
 * Les entrées de coffre à saisir en section coffre, avec leur motif.
 *
 * Les livraisons de monnaie et l'argent trouvé en sont RETIRÉS : ils se
 * saisissent en « autres opérations », bloc 2. Ils entrent bien au coffre, mais
 * COL MNC les attend ailleurs, et une somme saisie deux fois est plus longue à
 * retrouver qu'une somme oubliée.
 */
export function entreesASaisir(r: Rapport | null): LigneDictee[] {
  const inv = objet(r?.inventaireCoffre)
  return tableau(inv.entrees_coffre)
    .filter((e) => String(e.type) !== 'livraison' && String(e.type) !== 'trouve')
    .map((e) => ({
      cle: MOTIF_ENTREE[String(e.type)] ?? (String(e.label ?? '').trim() || 'Entrée'),
      source: String(e.label ?? '').trim() || undefined,
      valeur: cents(Number(e.amount) || 0),
    }))
}

const MOTIF_ENTREE: Record<string, string> = {
  SCO: 'Prélèvement SCO',
  FDC: 'Réintégration FDC',
}

// ── L'assemblage ───────────────────────────────────────────────────────────

export interface FeuilleSaisie {
  date: string
  colmnc: {
    apports: LigneDictee[]
    autres: LigneDictee[]
    ecarts: LigneDictee[]
    bons: LigneDictee[]
    ladCheques: { nb: number; total: number }
    ladAttente: { nb: number; total: number; lignes: LigneDictee[] }
    coffre: ColonneCoffre[]
    entrees: LigneDictee[]
  }
  cheques: Cheques
  lad: TotauxLad
}

/**
 * Tout ce que la feuille imprime, en une passe. Le document HTML ne fait
 * ensuite QUE de la mise en page : il ne filtre pas, il ne somme pas, il ne
 * décide pas. Un `if` dans un gabarit est une règle métier que personne ne
 * relit et qu'aucun test n'atteint.
 */
export function feuilleSaisie(
  date: string, r: Rapport | null, caissiers: Caissier[], padAttente: PadEnAttente[],
): FeuilleSaisie {
  const liv = livraisonsMonnaie(r)
  const totaux = totauxLad(r, padAttente)

  const autres: LigneDictee[] = []
  if (liv.nb > 0) {
    autres.push({
      cle: 'Livraison de monnaie',
      source: `${liv.nb} entrée${liv.nb > 1 ? 's' : ''} — étape 10`,
      valeur: liv.total,
    })
  }
  autres.push(...argentTrouve(r).map((l) => ({ ...l, cle: `Argent trouvé — ${l.cle}` })))

  return {
    date,
    colmnc: {
      apports: apportsFlottants(r, caissiers),
      autres,
      ecarts: ecartsASaisir(r),
      bons: bonsParGroupe(date, r),
      ladCheques: totaux.cheques,
      ladAttente: totaux.attente,
      coffre: colonnesCoffre(r),
      entrees: entreesASaisir(r),
    },
    cheques: chequesDuJour(r),
    lad: totaux,
  }
}
